import { useCanViewEmails } from '../../../../helpers/useCanViewEmails';
import React, { useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';

import { deleteUserAndTransferContent, getDeleteUserSourceImpact, TdeleteUserAndTransferContent } from '../../../../api/usersApi';
import { FOLDER_TYPE, RolesLowerCase, USER, USER_NEW, VIDEOFEED } from '../../../../enums';
import { ContainedButton, OutlinedButton } from '../../Buttons';
import FolderTransfer, { UserEXTENDED } from './FolderTransfer';
import UsersListSelection from '../../../UsersListSelection';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { NOTIFICATION } from '../../../../enums/ENUMS';
import { getFoldersByUserId, getFoldersByUserIdLightweight } from '../../../../api';
import { ReactComponent as FolderIcon } from '../../../../assets/icons/folder-purple-new.svg';
import { ReactComponent as ComposerIcon } from '../../../../assets/icons/composer.svg';
import styles from './DeleteUserModal.module.scss';
import { User } from '../../OrganizationCard';
import Modal from '../../Modal';
import { updateNotificationTrigger } from '../../../../store/sharedStore/sharedStoreSlice';

export type DeleteUserContentItem = (FOLDER_TYPE & { transferType?: 'folder' }) | (VIDEOFEED & { transferType: 'onlineComposer' });

const isOnlineComposerItem = (item: DeleteUserContentItem): item is Extract<DeleteUserContentItem, { transferType: 'onlineComposer' }> =>
  item.transferType === 'onlineComposer';
const getContentName = (item: DeleteUserContentItem) => (isOnlineComposerItem(item) ? item.title : item.name);
const getContentKey = (item: DeleteUserContentItem) => `${isOnlineComposerItem(item) ? 'onlineComposer' : 'folder'}-${item.id}`;
const getContentIcon = (item: DeleteUserContentItem) =>
  isOnlineComposerItem(item) ? (
    <ComposerIcon className={styles.contentIcon} style={{ color: '#39b3ea' }} />
  ) : (
    <FolderIcon className={styles.contentIcon} />
  );

type TDeleteUserModalProps = {
  isOpen: boolean;
  switchModal: () => void;
  user: USER;
  fetchData: () => void;
  orgId?: string;
};

enum STEPS {
  InitialModal = 0,
  InheritAll = 1,
  ListOfUsers = 2,
  ListOfFolders = 3,
  DeletionModal = 4,
}

enum SELECTED_DELETION_STEP {
  InheritAll = 'InheritAll',
  TransferToUser = 'TransferToUser',
  SelectedFolders = 'SelectedFolders',
  DeleteWithoutContent = 'DeleteWithoutContent',
}

const DeleteUserModal: React.FC<TDeleteUserModalProps> = ({ isOpen, switchModal, user, fetchData, orgId }) => {
  const canViewEmails = useCanViewEmails();
  const [loading, setLoading] = useState(false);
  const notification = useSnackbar();
  const { myOrganization, currentUser } = useAppSelector((state) => state.user);
  const [step, setStep] = useState<STEPS>(STEPS.InitialModal);
  const [userFolders, setUserFolders] = useState<FOLDER_TYPE[]>([]);
  const [userOnlineComposers, setUserOnlineComposers] = useState<DeleteUserContentItem[]>([]);
  const [selectedFolders, setSelectedFolders] = useState<FOLDER_TYPE[]>([]);
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [showSourceImpactModal, setShowSourceImpactModal] = useState(false);
  const [sourceImpactComposers, setSourceImpactComposers] = useState<string[]>([]);
  const pendingDeleteRef = useRef<(() => Promise<void>) | null>(null);
  const onClose = () => {
    switchModal();
    setSelectedFolders([]);
    setUserFolders([]);
    setUserOnlineComposers([]);
    setSelectedUser(null);
    setShowSourceImpactModal(false);
    setSourceImpactComposers([]);
    pendingDeleteRef.current = null;
    proceedToStep(STEPS.InitialModal);
    setCurrentStep(SELECTED_DELETION_STEP.InheritAll);
  };
  const [currentUserInList, setCurrentUserInList] = useState(false);

  const proceedToStep = (num: STEPS) => {
    setStep(num);
  };

  const handleOpenedState = async () => {
    try {
      setLoading(true);
      const organization_id = myOrganization?.id ? myOrganization.id : orgId;
      const res = await getFoldersByUserIdLightweight(user.id, organization_id);
      const folders = res.data.documents || [];
      const onlineComposers: DeleteUserContentItem[] = (res.data.onlineComposers || []).map((composer: VIDEOFEED) => ({
        ...composer,
        transferType: 'onlineComposer' as const,
      }));
      setUserFolders(folders);
      setUserOnlineComposers(onlineComposers);
      if (folders.length || onlineComposers.length) {
        setStep(STEPS.InitialModal);
      } else {
        setStep(STEPS.DeletionModal);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      handleOpenedState();
    }
  }, [isOpen]);

  const onFolderSelect = (folder: FOLDER_TYPE) => {
    if (selectedFolders.find((f) => f.id === folder.id)) {
      setSelectedFolders((prev) => prev.filter((f) => f.id !== folder.id));
    } else {
      setSelectedFolders([...selectedFolders, folder]);
    }
  };

  const getOrganizationId = () => String(orgId || myOrganization?.id || user?.organization?.id || '');

  const checkSourceDeleteImpact = async (body: TdeleteUserAndTransferContent, onConfirm: () => Promise<void>) => {
    try {
      setLoading(true);
      const res = await getDeleteUserSourceImpact(body, getOrganizationId());
      const impactedComposers = Array.isArray(res.data?.impactedComposers) ? res.data.impactedComposers : [];

      if (impactedComposers.length) {
        pendingDeleteRef.current = onConfirm;
        setSourceImpactComposers(impactedComposers);
        setShowSourceImpactModal(true);
        return true;
      }

      return false;
    } catch (err) {
      // @ts-ignore
      notification.showMessage(err?.response?.data?.message || 'Failed to check source file impact');
      return true;
    } finally {
      setLoading(false);
    }
  };

  const continueAfterSourceImpactWarning = async () => {
    const pendingDelete = pendingDeleteRef.current;
    pendingDeleteRef.current = null;
    setShowSourceImpactModal(false);
    setSourceImpactComposers([]);
    await pendingDelete?.();
  };

  const closeSourceImpactWarning = () => {
    pendingDeleteRef.current = null;
    setShowSourceImpactModal(false);
    setSourceImpactComposers([]);
  };

  const onInheritAll = async (selectedUserId?: string, skipSourceImpactCheck = false) => {
    let org_id = getOrganizationId();
    const body: TdeleteUserAndTransferContent = {
      ids: [{ id: user.id }],
      substitution: [
        {
          userId: selectedUserId ? selectedUserId : currentUser!.id,
          folderIds: selectedFolders.length > 0 ? selectedFolders.map((f) => f.id) : 'all',
          composerIds: userOnlineComposers.map((composer) => composer.id),
        },
      ],
    };

    if (!skipSourceImpactCheck && (await checkSourceDeleteImpact(body, () => onInheritAll(selectedUserId, true)))) {
      return;
    }

    try {
      setLoading(true);
      await deleteUserAndTransferContent(body, org_id);
      notification.showMessage('Done');
    } catch (err) {
      // @ts-ignore
      if (err?.response?.data?.message) {
        // @ts-ignore
        notification.showMessage(err.response.data.message);
      } else {
        console.log(err);
      }
    } finally {
      fetchData();
      onClose();
      setLoading(false);
    }
  };

  const handleDeleteWithoutContent = async (skipSourceImpactCheck = false) => {
    let org_id = getOrganizationId();
    const body: TdeleteUserAndTransferContent = {
      ids: [{ id: user.id }],
    };

    if (!skipSourceImpactCheck && (await checkSourceDeleteImpact(body, () => handleDeleteWithoutContent(true)))) {
      return;
    }

    try {
      setLoading(true);
      const res = await deleteUserAndTransferContent(body, org_id);
      if (res.status === 200) {
        notification.showMessage(NOTIFICATION.USER_DELETED);
        fetchData();
        onClose();
      }
    } catch (err) {
      console.log(err);
      // @ts-ignore
      if (err?.response?.data?.message) {
        // @ts-ignore
        notification.showMessage(err.response.data.message);
      } else {
        notification.showMessage(NOTIFICATION.NOT_DELETED);
      }
    } finally {
      setLoading(false);
    }
  };

  const [confirmModal, setConfirmModal] = useState(false);
  const [currentStep, setCurrentStep] = useState<SELECTED_DELETION_STEP>(SELECTED_DELETION_STEP.InheritAll);

  const closeConfirmModal = () => {
    setConfirmModal(false);
    onClose();
  };

  const proceedToDeleting = () => {
    if (selectedUser && selectedUser?.user.id && currentUser) {
      onInheritAll(selectedUser.user.id);
    }
    if (currentStep === SELECTED_DELETION_STEP.InheritAll) {
      onInheritAll();
    }
    if (currentStep === SELECTED_DELETION_STEP.DeleteWithoutContent) {
      handleDeleteWithoutContent();
    }
  };

  useEffect(() => {
    if (selectedUser) {
      setConfirmModal(true);
    }
  }, [selectedUser]);

  const dispatch = useAppDispatch();
  const onTransferAndDelete = async (userToDelete: USER, userListWithFolders: UserEXTENDED[], skipSourceImpactCheck = false) => {
    const usersAndFolders = userListWithFolders.filter((u) => u.folders?.length);
    let org_id = getOrganizationId();
    const body: TdeleteUserAndTransferContent = {
      ids: [{ id: userToDelete.id }],
      substitution: usersAndFolders.map((u) => ({
        userId: u.user.id,
        folderIds: u.folders!.filter((item) => !isOnlineComposerItem(item)).map((f) => f.id),
        composerIds: u.folders!.filter(isOnlineComposerItem).map((composer) => composer.id),
      })),
    };

    if (
      !skipSourceImpactCheck &&
      (await checkSourceDeleteImpact(body, () => onTransferAndDelete(userToDelete, userListWithFolders, true)))
    ) {
      return;
    }

    try {
      setLoading(true);
      await deleteUserAndTransferContent(body, org_id);
      fetchData();
      onClose();
    } catch (err) {
      console.log(err);
      // @ts-ignore
      if (err?.response?.data?.message) {
        // @ts-ignore
        notification.showMessage(err?.response?.data?.message);
      }
    } finally {
      setLoading(false);
      dispatch(updateNotificationTrigger(Date.now()));
    }
  };

  return (
    <Modal open={isOpen} onClose={onClose} loading={loading}>
      <div className={styles.modalBody}>
        {step === STEPS.InitialModal && (
          <>
            <div className={styles.modalTitle}>What to do with {user?.username || (canViewEmails ? user.email : 'User')}'s content?</div>
            <div className={styles.form}>
              <div className={styles.actionBtns}>
                {currentUser?.role.title !== RolesLowerCase.SUPER_ADMIN && (
                  <ContainedButton
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '32px' }}
                    disabled={loading}
                    onClick={() => {
                      setCurrentStep(SELECTED_DELETION_STEP.InheritAll);
                      setConfirmModal(true);
                    }}
                    kind="purple"
                  >
                    Inherit all
                  </ContainedButton>
                )}

                <ContainedButton
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '32px' }}
                  disabled={loading}
                  onClick={() => {
                    setCurrentStep(SELECTED_DELETION_STEP.TransferToUser);
                    proceedToStep(STEPS.ListOfUsers);
                    setCurrentUserInList(false);
                  }}
                  kind="purple"
                >
                  Allocate all to another OA/Analyst
                </ContainedButton>

                <ContainedButton
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '32px' }}
                  disabled={loading}
                  kind="purple"
                  onClick={() => {
                    setCurrentStep(SELECTED_DELETION_STEP.SelectedFolders);
                    proceedToStep(STEPS.ListOfFolders);
                    setCurrentUserInList(true);
                  }}
                >
                  <span>Select specific content </span>
                </ContainedButton>

                <OutlinedButton
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '32px' }}
                  disabled={loading}
                  kind="default"
                  onClick={() => {
                    setCurrentStep(SELECTED_DELETION_STEP.DeleteWithoutContent);
                    setConfirmModal(true);
                  }}
                >
                  <span>Delete user and all User’s Content</span>
                </OutlinedButton>
                <ContainedButton
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '32px' }}
                  disabled={loading}
                  kind="purple"
                  onClick={onClose}
                >
                  Cancel
                </ContainedButton>
              </div>
            </div>
          </>
        )}
        {step === STEPS.ListOfFolders && (
          <FolderTransfer
            onClose={onClose}
            onSubmit={onTransferAndDelete}
            user={user}
            userFolders={[...userFolders, ...userOnlineComposers]}
            setLoading={setLoading}
            loading={loading}
            orgId={orgId}
          />
        )}
        {step === STEPS.ListOfUsers && (
          <>
            <UsersListSelection
              currentUserInList={currentUserInList}
              filterUser={user as USER}
              parentLoading={loading}
              setSelectedUser={setSelectedUser}
              orgId={orgId!}
            />
            <div className={styles.form}>
              <div className={styles.actionBtns}>
                <ContainedButton kind="purple" style={{ height: '36px' }} disabled={loading} onClick={onClose}>
                  No, cancel
                </ContainedButton>
              </div>
            </div>
          </>
        )}
        {step === STEPS.DeletionModal && (
          <>
            <div className={styles.modalTitle}>Are you sure you want to delete {user?.username || (canViewEmails ? user.email : 'User')}?</div>
            <div className={styles.form}>
              <div className={styles.actionBtns}>
                <OutlinedButton onClick={handleDeleteWithoutContent} disabled={loading} kind="default">
                  Yes, I am sure
                </OutlinedButton>

                <ContainedButton kind="purple" disabled={loading} onClick={onClose}>
                  No, cancel
                </ContainedButton>
              </div>
            </div>
          </>
        )}
      </div>
      {confirmModal && (
        <Modal open={confirmModal} onClose={closeConfirmModal} loading={loading}>
          <div className={styles.modalBody}>
            <div className={styles.modalTitle}>Are you sure that you want to delete {user.username || (canViewEmails ? user.email : 'User')}? </div>
            <div>
              {currentStep === SELECTED_DELETION_STEP.InheritAll && (userFolders.length > 0 || userOnlineComposers.length > 0) && (
                <>
                  <div className={styles.listHeading}> This content will be transferred to you : </div>
                  <ul className={styles.folderList}>
                    {[...userFolders, ...userOnlineComposers].map((item) => (
                      <li className={styles.folder} key={getContentKey(item)}>
                        {getContentIcon(item)}
                        <span>"{getContentName(item)}"</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {currentStep === SELECTED_DELETION_STEP.SelectedFolders && selectedFolders.length > 0 && (
                <>
                  <div className={styles.listHeading}>
                    {' '}
                    {/* @ts-ignore */}
                    These folders will be transferred to {selectedUser?.user?.username || sselectedUser?.username || (canViewEmails ? selectedUser?.email : 'User')} :
                  </div>
                  <ul className={styles.folderList}>
                    {selectedFolders.map((f) => (
                      <li className={styles.folder} key={f.id}>
                        <span>"{f.name}"</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {currentStep === SELECTED_DELETION_STEP.TransferToUser && (userFolders.length > 0 || userOnlineComposers.length > 0) && (
                <>
                  <div className={styles.listHeading}>
                    {' '}
                    {/* @ts-ignore */}
                    This content will be transferred to {selectedUser?.user?.username || selectedUser?.username || (canViewEmails ? selectedUser?.email : 'User')} :
                  </div>
                  <ul className={styles.folderList}>
                    {[...userFolders, ...userOnlineComposers].map((item) => (
                      <li className={styles.folder} key={getContentKey(item)}>
                        {getContentIcon(item)}
                        <span>"{getContentName(item)}"</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {currentStep === SELECTED_DELETION_STEP.DeleteWithoutContent &&
                (userFolders.length > 0 || userOnlineComposers.length > 0) && (
                <>
                  <div className={styles.listHeading}> {user.username || (canViewEmails ? user.email : 'User')}'s content will be deleted:</div>
                  <ul className={styles.folderList}>
                    {[...userFolders, ...userOnlineComposers].map((item) => (
                      <li className={styles.folder} key={getContentKey(item)}>
                        {getContentIcon(item)}
                        <span>"{getContentName(item)}"</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
            <div className={styles.form}>
              <div className={styles.actionBtns}>
                <OutlinedButton
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '32px' }}
                  disabled={loading}
                  kind="default"
                  onClick={proceedToDeleting}
                >
                  <span>Delete user</span>
                </OutlinedButton>

                <ContainedButton
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '32px' }}
                  disabled={loading}
                  kind="purple"
                  onClick={closeConfirmModal}
                >
                  <span>Cancel</span>
                </ContainedButton>
              </div>
            </div>
          </div>
        </Modal>
      )}
      {showSourceImpactModal && (
        <Modal open={showSourceImpactModal} onClose={closeSourceImpactWarning} loading={loading}>
          <div className={styles.modalBody}>
            <div className={styles.modalTitle}>Deleting this user's source content will also affect these Online Composers.</div>
            <div className={styles.listHeading}>Composers that only depend on this source may be deleted.</div>
            <ul className={styles.folderList}>
              {sourceImpactComposers.map((composer, index) => (
                <li className={styles.folder} key={`${composer}-${index}`}>
                  <ComposerIcon className={styles.contentIcon} style={{ color: '#39b3ea' }} />
                  <span>"{composer}"</span>
                </li>
              ))}
            </ul>
            <div className={styles.form}>
              <div className={styles.actionBtns}>
                <OutlinedButton
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '32px' }}
                  disabled={loading}
                  kind="default"
                  onClick={continueAfterSourceImpactWarning}
                >
                  <span>Continue</span>
                </OutlinedButton>

                <ContainedButton
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '32px' }}
                  disabled={loading}
                  kind="purple"
                  onClick={closeSourceImpactWarning}
                >
                  <span>Cancel</span>
                </ContainedButton>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </Modal>
  );
};

export default DeleteUserModal;
